/**
 * Обработчик очереди команд от сервера
 * ОПТИМИЗИРОВАНО: timeout на выполнение команд для предотвращения зависаний
 */
import { apiClient } from '../services/apiClient'
import { LogsRepo } from './logsRepo'
import { EmployeesRepo } from './employeesRepo'
import { EventsRepo } from './eventsRepo'
import { useDeviceStore } from '../store/deviceStore'
import { withTimeout, TimeoutError } from '../utils/timeout'

// Timeout на выполнение команд (30 секунд)
const COMMAND_TIMEOUT_MS = 30000

export interface Command {
  id: string
  command_type: 'clear_cache' | 'sync_now' | 'update_config' | 
                'get_console_log' | 'take_screenshot' | 'get_sync_status' | 
                'create_manual_event' | 'force_reload' | string
  payload: Record<string, unknown>
}

export class QueueProcessor {
  /**
   * Обрабатывает команду от сервера
   */
  static async processCommand(command: Command): Promise<boolean> {
    try {
      const deviceStore = useDeviceStore.getState()

      if (!deviceStore.deviceId) {
        await LogsRepo.save('error', 'Cannot process command: device not registered')
        return false
      }

      // Подтверждаем получение команды (ack)
      await this.acknowledgeCommand(command.id)

      // Выполняем команду С TIMEOUT для предотвращения зависаний
      let success = false
      let resultData: Record<string, unknown> | undefined
      
      const executeWithTimeout = async () => {
        switch (command.command_type) {
          case 'clear_cache':
            return { success: await this.executeClearCache(command.payload) }
          case 'sync_now':
            return { success: await this.executeSyncNow() }
          case 'update_config':
            return { success: await this.executeUpdateConfig(command.payload) }
          case 'get_console_log':
            const logs = await this.executeGetConsoleLogs(command.payload)
            return { success: !!logs, resultData: logs }
          case 'take_screenshot':
            const screenshot = await this.executeTakeScreenshot()
            return { success: !!screenshot, resultData: screenshot }
          case 'get_sync_status':
            const status = await this.executeGetSyncStatus()
            return { success: !!status, resultData: status }
          case 'create_manual_event':
            return { success: await this.executeCreateManualEvent(command.payload) }
          case 'force_reload':
            return { success: await this.executeForceReload() }
          default:
            await LogsRepo.save('warn', `Unknown command type: ${command.command_type}`)
            return { success: false }
        }
      }
      
      try {
        const result = await withTimeout(
          executeWithTimeout(),
          COMMAND_TIMEOUT_MS,
          `Command ${command.command_type} timed out after ${COMMAND_TIMEOUT_MS}ms`
        )
        success = result.success
        resultData = result.resultData
      } catch (error) {
        if (error instanceof TimeoutError) {
          await LogsRepo.save('error', `Command ${command.command_type} TIMEOUT: ${error.message}`)
          console.error(`[QueueProcessor] ⏱️ TIMEOUT: ${command.command_type}`, error)
          success = false
        } else {
          throw error
        }
      }

      // Завершаем команду (complete)
      if (success) {
        await this.completeCommand(command.id, resultData)
      }

      return success
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error'
      await LogsRepo.save('error', `Failed to process command ${command.id}: ${errorMsg}`)
      return false
    }
  }

  /**
   * Подтверждает получение команды (ack)
   */
  private static async acknowledgeCommand(commandId: string): Promise<void> {
    try {
      await apiClient.post(`/tablet/commands/${commandId}/ack`, {})
      await LogsRepo.save('info', `Command ${commandId} acknowledged`)
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error'
      await LogsRepo.save('error', `Failed to acknowledge command ${commandId}: ${errorMsg}`)
      throw error
    }
  }

  /**
   * Завершает выполнение команды (complete)
   */
  private static async completeCommand(
    commandId: string, 
    resultData?: Record<string, unknown>
  ): Promise<void> {
    try {
      await apiClient.post(`/tablet/commands/${commandId}/complete`, {
        success: true,
        result_data: resultData
      })
      await LogsRepo.save('info', `Command ${commandId} completed`)
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error'
      await LogsRepo.save('error', `Failed to complete command ${commandId}: ${errorMsg}`)
      // Не бросаем ошибку, так как команда уже выполнена
    }
  }

  /**
   * Выполняет команду clear_cache
   * КРИТИЧНО: После очистки СРАЗУ запускаем синхронизацию!
   */
  private static async executeClearCache(payload: Record<string, unknown>): Promise<boolean> {
    try {
      const cacheType = payload.cache_type as string | undefined
      let clearedEmployees = false

      if (cacheType === 'employees' || !cacheType) {
        // Очищаем кеш публичных ключей
        const allKeys = await EmployeesRepo.getAllPublicKeys()
        const employeeIds = allKeys.map((k) => k.employee_id)
        
        // Используем db напрямую для удаления
        const { db } = await import('./db')
        await db.employees_cache.bulkDelete(employeeIds)
        
        await LogsRepo.save('info', `Cleared employees cache (${employeeIds.length} keys)`)
        clearedEmployees = true
      }

      if (cacheType === 'events' || !cacheType) {
        // Очищаем старые синхронизированные события (старше 30 дней)
        const deletedCount = await EventsRepo.deleteOldSynced(30)
        await LogsRepo.save('info', `Cleared old synced events (${deletedCount} events)`)
      }

      // Очищаем таблицу логов и in-memory буферы.
      // Накопленные за неделю логи фрагментируют SQLite (IndexedDB) → все IDB-операции
      // замедляются → QR-сканирование подвисает на 15–30 секунд.
      if (cacheType === 'logs' || !cacheType) {
        try {
          const { db } = await import('./db')
          const { clearLogsBuffer } = await import('../utils/consoleCapture')
          // Сначала сбрасываем in-memory батч, чтобы не записать его после очистки таблицы
          clearLogsBuffer()
          await db.logs.clear()
          // Также очищаем ETag-кеш синхронизации сотрудников из localStorage
          localStorage.removeItem('syncqueue_etag')
          console.log('[QueueProcessor] ✅ Logs table and memory buffers cleared')
          // Логируем уже после очистки — это первая запись в чистую таблицу
          await LogsRepo.save('info', 'Logs cleared via clear_cache command (SQLite defrag)')
        } catch (logsErr) {
          // Очистка логов некритична — не прерываем команду
          const msg = logsErr instanceof Error ? logsErr.message : String(logsErr)
          console.warn('[QueueProcessor] ⚠️ Could not clear logs (non-critical):', msg)
        }
      }

      // 🚨 КРИТИЧНО: Если очистили кеш сотрудников - СРАЗУ синхронизируем!
      // Без этого пользователи получат "СОТРУДНИК НЕ НАЙДЕН"
      if (clearedEmployees) {
        console.log('[QueueProcessor] 🔄 Starting immediate sync after cache clear...')
        await LogsRepo.save('info', 'Starting immediate sync after cache clear')
        
        try {
          const { EmployeesService } = await import('../services/employeesService')
          const deviceStore = useDeviceStore.getState()
          
          if (deviceStore.siteId) {
            const employees = await EmployeesService.fetchAndCacheFullData(deviceStore.siteId)
            console.log(`[QueueProcessor] ✅ Immediate sync complete: ${employees.length} employees cached`)
            await LogsRepo.save('info', `Immediate sync after clear: ${employees.length} employees cached`)
          } else {
            console.warn('[QueueProcessor] ⚠️ No siteId, cannot sync after clear')
            await LogsRepo.save('warn', 'Cannot sync after clear: no siteId')
          }
        } catch (syncError) {
          const syncErrorMsg = syncError instanceof Error ? syncError.message : 'Unknown sync error'
          console.error('[QueueProcessor] ❌ Immediate sync failed:', syncError)
          await LogsRepo.save('error', `Immediate sync after clear failed: ${syncErrorMsg}`)
          // Не возвращаем false - очистка прошла успешно, sync может быть временно недоступен
        }
      }

      return true
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error'
      await LogsRepo.save('error', `Failed to clear cache: ${errorMsg}`)
      return false
    }
  }

  /**
   * Выполняет команду sync_now
   */
  private static async executeSyncNow(): Promise<boolean> {
    try {
      const { SyncService } = await import('../services/syncService')
      const result = await SyncService.syncEvents()
      
      await LogsRepo.save(
        'info',
        `Manual sync completed: saved=${result.saved}, duplicates=${result.duplicates}, errors=${result.errors}`
      )
      
      return result.success
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error'
      await LogsRepo.save('error', `Failed to sync: ${errorMsg}`)
      return false
    }
  }

  /**
   * Выполняет команду update_config
   */
  private static async executeUpdateConfig(payload: Record<string, unknown>): Promise<boolean> {
    try {
      const deviceStore = useDeviceStore.getState()

      // Обновляем конфигурацию устройства
      // Версия теперь берется из package.json через import.meta.env.VITE_APP_VERSION
      // и не требует обновления через команду

      if (payload.site_id && typeof payload.site_id === 'string') {
        deviceStore.setSiteId(payload.site_id)
      }

      await LogsRepo.save('info', 'Config updated from server command')
      return true
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error'
      await LogsRepo.save('error', `Failed to update config: ${errorMsg}`)
      return false
    }
  }

  /**
   * Получает логи для удалённой диагностики.
   * Возвращает:
   * 1. Последние N записей из памяти (текущая сессия)
   * 2. Последние 50 ERROR записей из IndexedDB (включая прошлые сессии и pre-React ошибки)
   * Это гарантирует видимость критических ошибок даже если они произошли давно.
   */
  private static async executeGetConsoleLogs(
    payload: Record<string, unknown>
  ): Promise<Record<string, unknown> | undefined> {
    try {
      const limit = (payload.limit as number) || 100
      const level = payload.level as 'info' | 'warn' | 'error' | undefined
      
      // 1. Память: текущая сессия (быстро)
      const { getMemoryLogs } = await import('../utils/consoleCapture')
      const memLogs = getMemoryLogs({ level, limit })
      
      // 2. IndexedDB: последние ошибки за последние 24h (включая прошлые сессии)
      const dbErrors = await LogsRepo.getAll({ level: 'error', limit: 50 })
      
      // 3. IndexedDB: последние warn за 24h
      const dbWarns = await LogsRepo.getAll({ level: 'warn', limit: 30 })
      
      // 'logs' — поле backward-compat для admin-panel (ожидает именно это имя)
      // Возвращаем все memory logs как основной список + отдельно ошибки из IDB
      return {
        logs: memLogs,
        db_errors: dbErrors,
        db_warns: dbWarns,
        summary: {
          memory_count: memLogs.length,
          db_errors_count: dbErrors.length,
          db_warns_count: dbWarns.length
        }
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error'
      await LogsRepo.save('error', `Failed to get console logs: ${errorMsg}`)
      return undefined
    }
  }

  /**
   * Делает скриншот DOM через html2canvas (мгновенно, без запроса разрешений)
   * ОПТИМИЗИРОВАНО: полный viewport без искажений
   */
  private static async executeTakeScreenshot(): Promise<Record<string, unknown> | undefined> {
    try {
      console.log('[QueueProcessor] Taking DOM snapshot...')
      
      // Динамический импорт html2canvas
      const html2canvas = (await import('html2canvas')).default
      
      // Получаем реальные размеры viewport
      const targetElement = document.documentElement || document.body
      
      // КРИТИЧНО: используем полный document.documentElement для точного рендера
      const canvas = await html2canvas(targetElement, {
        allowTaint: false,
        useCORS: true,
        logging: false,
        scale: window.devicePixelRatio || 1, // Используем DPR устройства для четкости
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        scrollX: 0,
        scrollY: 0,
        x: 0,
        y: 0,
        backgroundColor: '#ffffff',
        imageTimeout: 0, // Без ожидания загрузки изображений
        removeContainer: true,
      })
      
      // Конвертируем в base64 (JPEG 0.85 для баланса качества/размера)
      const screenshotBase64 = canvas.toDataURL('image/jpeg', 0.85).split(',')[1]
      
      console.log(`[QueueProcessor] ✅ Screenshot: ${canvas.width}x${canvas.height}, ${(screenshotBase64.length / 1024).toFixed(0)}KB`)
      
      return { 
        screenshot: screenshotBase64,
        timestamp: new Date().toISOString(),
        resolution: `${canvas.width}x${canvas.height}`,
        format: 'jpeg',
        quality: 0.85,
        devicePixelRatio: window.devicePixelRatio || 1
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error'
      console.error(`[QueueProcessor] ❌ Screenshot failed: ${errorMsg}`)
      await LogsRepo.save('error', `Screenshot error: ${errorMsg}`)
      return undefined
    }
  }

  /**
   * Получает статус синхронизации и базы данных
   * ОПТИМИЗИРОВАНО: параллельные запросы к IndexedDB
   */
  private static async executeGetSyncStatus(): Promise<Record<string, unknown> | undefined> {
    try {
      const { SyncService } = await import('../services/syncService')
      const { db } = await import('./db')
      
      // ПАРАЛЛЕЛЬНЫЕ запросы для ускорения (вместо последовательных)
      const [unsyncedCount, eventsCount, employeesCount, logsCount] = await Promise.all([
        EventsRepo.getUnsyncedCount(),
        db.events.count(),
        db.employees_cache.count(),
        db.logs.count()
      ])
      
      const lastSyncResult = SyncService.getLastResult()
      
      console.log(`[QueueProcessor] ✅ Sync status: ${unsyncedCount} unsynced, ${eventsCount} events total`)
      
      return {
        sync: {
          unsynced_events: unsyncedCount,
          last_sync: lastSyncResult,
          sync_service_status: SyncService.getStatus()
        },
        database: {
          events: eventsCount,
          employees: employeesCount,
          logs: logsCount
        },
        timestamp: new Date().toISOString()
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error'
      console.error(`[QueueProcessor] ❌ Sync status failed: ${errorMsg}`)
      await LogsRepo.save('error', `Sync status error: ${errorMsg}`)
      return undefined
    }
  }

  /**
   * Создаёт ручное событие удалённо.
   *
   * Поддерживает два режима:
   * 1. Обычный (from admin panel): happened_at = текущее время, событие ставится в очередь синхронизации.
   * 2. Авто-закрытие (already_synced=true): happened_at берётся из payload (серверное время),
   *    событие сохраняется с synced=1 — повторная отправка на сервер не происходит,
   *    так как авто-закрытие уже записано в БД сервером (Celery-задача).
   */
  private static async executeCreateManualEvent(payload: Record<string, unknown>): Promise<boolean> {
    try {
      const employeeId = payload.employee_id as string
      const eventType = payload.event_type as 'check_in' | 'check_out'
      const alreadySynced = payload.already_synced === true
      const deviceStore = useDeviceStore.getState()
      
      if (!deviceStore.siteId || !deviceStore.deviceId) {
        throw new Error('Device not registered')
      }
      
      let happenedAt: string
      if (alreadySynced && typeof payload.happened_at === 'string' && payload.happened_at) {
        // Авто-закрытие: используем временну́ю метку от сервера
        happenedAt = payload.happened_at
      } else {
        // Обычная команда от администратора: текущее локальное время
        const { toMSKISOString } = await import('../utils/timezone')
        happenedAt = toMSKISOString()
      }
      
      const { CryptoService } = await import('../services/cryptoService')
      const payloadHash = await CryptoService.calculatePayloadHash({
        employee_id: employeeId,
        site_id: deviceStore.siteId,
        device_id: deviceStore.deviceId,
        event_type: eventType,
        happened_at: happenedAt,
        qr_token: ''
      })
      
      await EventsRepo.save(
        {
          employee_id: employeeId,
          site_id: deviceStore.siteId,
          device_id: deviceStore.deviceId,
          event_type: eventType,
          happened_at: happenedAt,
          payload_hash: payloadHash,
          source: 'manual'
        },
        { alreadySynced }
      )
      
      await LogsRepo.save(
        'info',
        `Remote manual event created: ${eventType} for ${employeeId}` +
          (alreadySynced ? ' [already_synced, no upload]' : '')
      )
      
      // Немедленная синхронизация только для обычных команд (не авто-закрытий)
      if (!alreadySynced && navigator.onLine) {
        const { SyncService } = await import('../services/syncService')
        SyncService.syncNow().catch((syncError) => {
          console.error('[QueueProcessor] Failed to sync remote manual event immediately:', syncError)
        })
      }
      
      return true
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error'
      await LogsRepo.save('error', `Failed to create manual event: ${errorMsg}`)
      return false
    }
  }

  /**
   * Принудительное обновление PWA: очищаем кеши и перезагружаем.
   *
   * ВАЖНО: НЕ разрегистрируем SW.
   * Разрегистрация → новый SW активируется → clients.claim() → controllerchange →
   * vite-plugin-pwa делает reload → новый SW снова активируется → бесконечный цикл.
   *
   * Вместо этого: очищаем кеши → reload → существующий SW обрабатывает navigation
   * через NetworkFirst → идёт в сеть (кеш пуст) → грузит свежие файлы.
   */
  private static async executeForceReload(): Promise<boolean> {
    try {
      console.log('[QueueProcessor] 🔄 FORCE RELOAD: clearing caches and reloading...')
      await LogsRepo.save('info', 'Force reload: clearing all caches...')

      // Очищаем все кеши (navigation-cache, api-cache, precache)
      if ('caches' in window) {
        const cacheNames = await caches.keys()
        await Promise.all(cacheNames.map(name => caches.delete(name)))
        console.log(`[QueueProcessor] ✅ ${cacheNames.length} cache(s) cleared`)
      }

      await LogsRepo.save('info', 'Force reload: caches cleared, reloading...')

      // Reload — SW (уже активный) получит navigation-запрос, кеш пуст →
      // NetworkFirst пойдёт в сеть → получит свежий index.html → новая версия.
      setTimeout(() => {
        window.location.reload()
      }, 500)

      return true
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error'
      console.error('[QueueProcessor] ❌ Force reload failed:', errorMsg)
      await LogsRepo.save('error', `Force reload failed: ${errorMsg}`)
      return false
    }
  }

  /**
   * Обрабатывает массив команд
   */
  static async processCommands(commands: Command[]): Promise<void> {
    for (const command of commands) {
      try {
        await this.processCommand(command)
      } catch (error) {
        console.error(`Failed to process command ${command.id}:`, error)
        // Продолжаем обработку остальных команд
      }
    }
  }
}

